import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { parseLocalDate, todayLocalIso, addDaysIso, toLocalIso } from "@/lib/dateUtils";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Sparkles, Wand2, RefreshCw, UserX, Users, X, Home,
  ClipboardCheck, Repeat, CheckCircle2, Save, Undo2, Download, Mail, FileText, Filter,
} from "lucide-react";
import { toast } from "sonner";
import { TRADES, tradeMeta, LEAVE_KINDS, leaveMeta } from "@/pages/Cleaners";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

// 2-week helper calendar. Rows = cleaners, columns = days. Emerald cells are
// booking assignments, blue cells are helper tasks. Click any cell to pick a
// different helper or clear the assignment. Drag a cell onto another row to
// move it.
//
// All mutations are buffered locally and only POSTed when the coordinator
// clicks "Save changes" — the grid never auto-refreshes mid-edit.

// Use local-date formatting so the day-key matches the day-name shown in
// the column header. Otherwise `toISOString().slice(0,10)` returns the UTC
// date which can be a day off for users near midnight UTC.
const fmt = (d) => toLocalIso(d);

const emptyPending = () => ({ segments: {}, tasks: {}, taskStatus: {} });

export default function HelpersCalendar() {
  const navigate = useNavigate();
  const [baseData, setBaseData] = useState(null);
  const [cleaners, setCleaners] = useState([]);
  const [from, setFrom] = useState(() => todayIso());
  const [to, setTo] = useState(() => addDays(todayIso(), 13));
  const [strategy, setStrategy] = useState("least_busy");
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [pending, setPending] = useState(emptyPending);
  const [saving, setSaving] = useState(false);
  // Phase 53 — Operations module
  const [filterCategory, setFilterCategory] = useState("all");
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailDraft, setEmailDraft] = useState({ staff_ids: [], extra_recipients: "", subject: "", message: "" });
  const [emailSending, setEmailSending] = useState(false);
  const dragRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const catParam = filterCategory && filterCategory !== "all" ? `&category=${filterCategory}` : "";
      const [cal, cl] = await Promise.all([
        api.get(`/housing/cleaners/calendar?from_date=${from}&to_date=${to}${catParam}`),
        api.get("/housing/cleaners"),
      ]);
      setBaseData(cal.data);
      setCleaners(cl.data);
      setPending(emptyPending());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  }, [from, to, filterCategory]);
  useEffect(() => { load(); }, [load]);

  const pendingCount = useMemo(() => (
    Object.keys(pending.segments).length +
    Object.keys(pending.tasks).length +
    Object.keys(pending.taskStatus).length
  ), [pending]);

  // Browser-level guard — beforeunload fires when the tab/window closes or
  // navigates externally. Modern browsers ignore custom messages but the
  // "are you sure" prompt still appears when returnValue is set.
  useEffect(() => {
    if (pendingCount === 0) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [pendingCount]);

  // Apply buffered changes on top of the API snapshot. The grid renders from
  // this derived view so coordinators see their edits live without a server
  // round-trip per click.
  const data = useMemo(() => {
    if (!baseData) return null;
    if (pendingCount === 0) return baseData;
    const clone = JSON.parse(JSON.stringify(baseData));
    const cleanerIdToRow = new Map(clone.cleaners.map((c) => [c.id, c]));

    // Segment moves
    Object.entries(pending.segments).forEach(([sid, target]) => {
      let seg = null;
      for (const row of clone.cleaners) {
        const idx = row.assignments.findIndex((a) => a.segment_id === sid);
        if (idx >= 0) { seg = row.assignments[idx]; row.assignments.splice(idx, 1); break; }
      }
      if (!seg) {
        const idx = clone.unassigned.findIndex((a) => a.segment_id === sid);
        if (idx >= 0) { seg = clone.unassigned[idx]; clone.unassigned.splice(idx, 1); }
      }
      if (!seg) return;
      if (target === null) {
        clone.unassigned.push(seg);
      } else {
        const dest = cleanerIdToRow.get(target);
        if (dest) dest.assignments.push(seg);
      }
    });

    // Task moves
    Object.entries(pending.tasks).forEach(([tid, target]) => {
      let task = null;
      for (const row of clone.cleaners) {
        const idx = (row.tasks || []).findIndex((t) => t.task_id === tid);
        if (idx >= 0) { task = row.tasks[idx]; row.tasks.splice(idx, 1); break; }
      }
      if (!task) {
        const idx = (clone.unassigned_tasks || []).findIndex((t) => t.task_id === tid);
        if (idx >= 0) { task = clone.unassigned_tasks[idx]; clone.unassigned_tasks.splice(idx, 1); }
      }
      if (!task) return;
      if (target === null) {
        (clone.unassigned_tasks ||= []).push(task);
      } else {
        const dest = cleanerIdToRow.get(target);
        if (dest) (dest.tasks ||= []).push(task);
      }
    });

    // Task status toggles
    Object.entries(pending.taskStatus).forEach(([key, status]) => {
      const sep = key.indexOf("|");
      const tid = key.slice(0, sep);
      const date = key.slice(sep + 1);
      const apply = (list) => (list || []).forEach((t) => {
        if (t.task_id === tid && t.date === date) t.occurrence_status = status;
      });
      clone.cleaners.forEach((c) => apply(c.tasks));
      apply(clone.unassigned_tasks);
    });

    // Recompute load counters per cleaner so the right-hand totals stay accurate
    clone.cleaners.forEach((c) => {
      const units = new Set();
      let nights = 0;
      (c.assignments || []).forEach((a) => {
        if (a.unit_id) units.add(a.unit_id);
        nights += (a.nights || 0);
      });
      c.load_units = units.size;
      c.load_nights = nights;
      c.load_tasks = (c.tasks || []).length;
    });
    clone.unassigned_count = clone.unassigned.length;
    clone.unassigned_tasks_count = (clone.unassigned_tasks || []).length;
    return clone;
  }, [baseData, pending, pendingCount]);

  const days = useMemo(() => {
    const out = [];
    if (!data) return out;
    const start = parseLocalDate(data.from_date);
    const end = parseLocalDate(data.to_date);
    while (start <= end) { out.push(new Date(start)); start.setDate(start.getDate() + 1); }
    return out;
  }, [data]);

  const activeHelpers = useMemo(() => cleaners.filter((c) => c.active), [cleaners]);

  // Cells for a single day. Assignments are half-open intervals [from, to).
  const assignmentsFor = (assignments, dayStr) => assignments.filter(
    (a) => a.from_date <= dayStr && a.to_date > dayStr,
  );
  const tasksFor = (tasks, dayStr) => (tasks || []).filter((t) => t.date === dayStr);
  // Phase 54 — leave windows are inclusive on both ends.
  const leaveFor = (leaves, dayStr) => (leaves || []).find(
    (lv) => lv.from_date <= dayStr && lv.to_date >= dayStr,
  );

  const autoAssign = async () => {
    if (data?.unassigned_count === 0) {
      toast.info("Everything is already assigned in this window.");
      return;
    }
    if (pendingCount > 0) {
      toast.error("Save or discard your pending changes before auto-assigning.");
      return;
    }
    if (!window.confirm(`Auto-assign ${data?.unassigned_count} unassigned segment${data?.unassigned_count === 1 ? "" : "s"} using "${strategy.replace("_", " ")}"?`)) return;
    setRunning(true);
    try {
      const { data: r } = await api.post(`/housing/cleaners/auto-assign?from_date=${from}&to_date=${to}&strategy=${strategy}`);
      toast.success(`${r.assigned_count} segment${r.assigned_count === 1 ? "" : "s"} assigned via ${r.strategy.replace("_", " ")}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Auto-assign failed"); }
    finally { setRunning(false); }
  };

  // Buffered mutations — record the intended change, optimistic UI updates
  // the grid, but no API call is made until the coordinator clicks "Save".
  const reassignSegment = (bookingId, segmentId, helperId) => {
    // Phase 54 — warn if the target staff member is on leave for this segment
    if (helperId && baseData) {
      const target = baseData.cleaners.find((c) => c.id === helperId);
      let segFrom = null, segTo = null;
      for (const c of baseData.cleaners) {
        const a = c.assignments.find((a) => a.segment_id === segmentId);
        if (a) { segFrom = a.from_date; segTo = a.to_date; break; }
      }
      if (!segFrom) {
        const a = (baseData.unassigned || []).find((a) => a.segment_id === segmentId);
        if (a) { segFrom = a.from_date; segTo = a.to_date; }
      }
      if (target && segFrom && segTo) {
        const overlap = (target.leaves || []).find((lv) => lv.from_date <= segTo && lv.to_date >= segFrom);
        if (overlap) {
          const ok = window.confirm(
            `${target.name} is on ${leaveMeta(overlap.kind).label.toLowerCase()} ` +
            `from ${overlap.from_date} → ${overlap.to_date}. Assign anyway?`
          );
          if (!ok) return;
        }
      }
    }
    setPending((prev) => {
      const next = { ...prev, segments: { ...prev.segments } };
      next.segments[segmentId] = helperId || null;
      return next;
    });
  };
  const reassignTask = (taskId, helperId) => {
    if (helperId && baseData) {
      const target = baseData.cleaners.find((c) => c.id === helperId);
      let taskDate = null;
      for (const c of baseData.cleaners) {
        const t = (c.tasks || []).find((tt) => tt.task_id === taskId);
        if (t) { taskDate = t.date; break; }
      }
      if (!taskDate) {
        const t = (baseData.unassigned_tasks || []).find((tt) => tt.task_id === taskId);
        if (t) taskDate = t.date;
      }
      if (target && taskDate) {
        const overlap = (target.leaves || []).find((lv) => lv.from_date <= taskDate && lv.to_date >= taskDate);
        if (overlap) {
          const ok = window.confirm(
            `${target.name} is on ${leaveMeta(overlap.kind).label.toLowerCase()} on ${taskDate}. Assign anyway?`
          );
          if (!ok) return;
        }
      }
    }
    setPending((prev) => {
      const next = { ...prev, tasks: { ...prev.tasks } };
      next.tasks[taskId] = helperId || null;
      return next;
    });
  };
  const markTaskDone = (taskId, occurrenceDate, newStatus) => {
    setPending((prev) => {
      const next = { ...prev, taskStatus: { ...prev.taskStatus } };
      next.taskStatus[`${taskId}|${occurrenceDate}`] = newStatus;
      return next;
    });
  };

  const discardChanges = () => {
    if (pendingCount === 0) return;
    if (!window.confirm(`Discard ${pendingCount} pending change${pendingCount === 1 ? "" : "s"}?`)) return;
    setPending(emptyPending());
    toast.info("Pending changes discarded");
  };

  // Phase 53 — Operations exports & email
  const downloadSchedule = async (format /* "pdf" | "csv" */) => {
    try {
      const params = new URLSearchParams({ from_date: from, to_date: to, format });
      if (filterCategory && filterCategory !== "all") params.set("category", filterCategory);
      const r = await api.get(`/operations/schedule/export?${params}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: format === "pdf" ? "application/pdf" : "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ops_schedule_${filterCategory || "all"}_${from}_to_${to}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    }
    setExportMenuOpen(false);
  };

  const sendScheduleEmail = async () => {
    const extras = emailDraft.extra_recipients
      .split(/[,;\s]+/)
      .map((s) => s.trim())
      .filter((s) => s && /^[^@]+@[^@]+\.[^@]+$/.test(s));
    if (emailDraft.staff_ids.length === 0 && extras.length === 0) {
      toast.error("Pick at least one staff member or add a free-text email");
      return;
    }
    setEmailSending(true);
    try {
      const { data } = await api.post("/operations/schedule/email", {
        from_date: from,
        to_date: to,
        category: filterCategory && filterCategory !== "all" ? filterCategory : null,
        staff_ids: emailDraft.staff_ids,
        extra_recipients: extras,
        subject: emailDraft.subject || "",
        message: emailDraft.message || "",
      });
      toast.success(`Sent to ${data.sent_to.length} recipient(s) · ${data.rows} job(s) attached`);
      if (data.skipped?.length) {
        toast.info(`Skipped (no email on file): ${data.skipped.join(", ")}`);
      }
      setEmailOpen(false);
      setEmailDraft({ staff_ids: [], extra_recipients: "", subject: "", message: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Email failed");
    } finally {
      setEmailSending(false);
    }
  };

  const saveChanges = async () => {
    if (pendingCount === 0) return;
    setSaving(true);
    let okCount = 0;
    let failCount = 0;
    const segCalls = Object.entries(pending.segments).map(async ([sid, helperId]) => {
      // We need the booking_id for the segment endpoint — locate it from baseData
      let bookingId = null;
      for (const c of baseData.cleaners) {
        const a = c.assignments.find((a) => a.segment_id === sid);
        if (a) { bookingId = a.booking_id; break; }
      }
      if (!bookingId) {
        const a = baseData.unassigned.find((a) => a.segment_id === sid);
        if (a) bookingId = a.booking_id;
      }
      if (!bookingId) { failCount++; return; }
      const cid = helperId ? `?cleaner_id=${helperId}` : "";
      try {
        await api.put(`/housing/segments/${bookingId}/${sid}/cleaner${cid}`);
        okCount++;
      } catch { failCount++; }
    });
    const taskCalls = Object.entries(pending.tasks).map(async ([tid, helperId]) => {
      const hid = helperId ? `?helper_id=${helperId}` : "";
      try {
        await api.put(`/housing/helper-tasks/${tid}/assign${hid}`);
        okCount++;
      } catch { failCount++; }
    });
    const statusCalls = Object.entries(pending.taskStatus).map(async ([key, status]) => {
      const sep = key.indexOf("|");
      const tid = key.slice(0, sep);
      const date = key.slice(sep + 1);
      try {
        await api.put(`/housing/helper-tasks/${tid}/status?status=${status}&occurrence_date=${date}`);
        okCount++;
      } catch { failCount++; }
    });
    await Promise.all([...segCalls, ...taskCalls, ...statusCalls]);
    if (failCount === 0) {
      toast.success(`Saved ${okCount} change${okCount === 1 ? "" : "s"}`);
    } else {
      toast.error(`${okCount} saved · ${failCount} failed — reloading to show server state`);
    }
    setSaving(false);
    load();
  };

  // HTML5 drag-and-drop — payload describes the cell (booking segment OR task)
  // so we can hit the right endpoint on drop.
  const onDragStart = (e, payload) => {
    dragRef.current = payload;
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", JSON.stringify(payload)); } catch {}
  };
  const onRowDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onRowDrop = (e, targetHelperId) => {
    e.preventDefault();
    const p = dragRef.current;
    dragRef.current = null;
    if (!p) return;
    if (p.kind === "segment") {
      if (p.cleanerId === targetHelperId) return;
      reassignSegment(p.bookingId, p.segmentId, targetHelperId);
    } else if (p.kind === "task") {
      if (p.helperId === targetHelperId) return;
      reassignTask(p.taskId, targetHelperId);
    }
  };

  if (loading || !data) return <div className="p-6 text-[#7A8A84]">Loading helper calendar…</div>;

  return (
    <div className="space-y-6" data-testid="helpers-calendar-page">
      <Button variant="ghost" onClick={() => {
        if (pendingCount > 0 && !window.confirm(`Discard ${pendingCount} unsaved change${pendingCount === 1 ? "" : "s"}?`)) return;
        navigate(-1);
      }} className="text-[#7A8A84]" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back
      </Button>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1C1F1E] flex items-center gap-2">
            <Users className="h-6 w-6 text-[#40916C]" /> Operations Calendar
          </h1>
          <p className="text-sm text-[#7A8A84]">Housekeeping · Groundskeeping · Maintenance · Plumbing · Electrical — click any cell to reassign or clear · drag cells between rows to rebalance · changes are buffered locally until you click <span className="font-semibold text-[#1B4332]">Save</span>.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => navigate("/helper-tasks")} className="border-[#1B4332]/20 text-[#1B4332]" data-testid="open-tasks-btn">
            <ClipboardCheck className="h-3.5 w-3.5 mr-1" /> Work orders
          </Button>
          {/* Phase 53 — export & email */}
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setExportMenuOpen((v) => !v)}
              className="border-[#1B4332]/20 text-[#1B4332]"
              data-testid="export-schedule-btn"
            >
              <Download className="h-3.5 w-3.5 mr-1" /> Export
            </Button>
            {exportMenuOpen && (
              <div className="absolute right-0 top-full mt-1 z-30 bg-white border border-[#E8E8E6] rounded-lg shadow-lg w-44 py-1" data-testid="export-menu">
                <button
                  onClick={() => downloadSchedule("pdf")}
                  className="w-full text-left text-sm px-3 py-1.5 hover:bg-[#F4F4F1] inline-flex items-center gap-2"
                  data-testid="export-pdf-btn"
                >
                  <FileText className="h-3.5 w-3.5" /> PDF schedule
                </button>
                <button
                  onClick={() => downloadSchedule("csv")}
                  className="w-full text-left text-sm px-3 py-1.5 hover:bg-[#F4F4F1] inline-flex items-center gap-2"
                  data-testid="export-csv-btn"
                >
                  <FileText className="h-3.5 w-3.5" /> CSV (Excel)
                </button>
              </div>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEmailOpen(true)}
            className="border-[#1B4332]/20 text-[#1B4332]"
            data-testid="email-schedule-btn"
          >
            <Mail className="h-3.5 w-3.5 mr-1" /> Email
          </Button>
          <Input
            type="date"
            value={from}
            onChange={(e) => {
              if (pendingCount > 0 && !window.confirm("Discard unsaved changes and reload the calendar?")) return;
              setFrom(e.target.value);
            }}
            className="w-40"
            data-testid="from-date-input"
          />
          <span className="text-[#7A8A84]">→</span>
          <Input
            type="date"
            value={to}
            onChange={(e) => {
              if (pendingCount > 0 && !window.confirm("Discard unsaved changes and reload the calendar?")) return;
              setTo(e.target.value);
            }}
            className="w-40"
            data-testid="to-date-input"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (pendingCount > 0 && !window.confirm("Discard unsaved changes and reload from the server?")) return;
              load();
            }}
            data-testid="refresh-btn"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Phase 53 — Trade filter pills */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="trade-filter-bar">
        <span className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-[#7A8A84] font-medium">
          <Filter className="h-3 w-3" /> Trade
        </span>
        <button
          onClick={() => {
            if (pendingCount > 0 && !window.confirm("Discard unsaved changes and reload the calendar?")) return;
            setFilterCategory("all");
          }}
          className={`px-2.5 py-1 rounded-full text-xs font-medium border transition ${
            filterCategory === "all"
              ? "bg-[#1B4332] text-white border-[#1B4332]"
              : "bg-white text-[#525C58] border-[#E8E8E6] hover:bg-[#F4F4F1]"
          }`}
          data-testid="trade-filter-all"
        >
          All
        </button>
        {TRADES.map((t) => {
          const Icon = t.icon;
          const on = filterCategory === t.k;
          return (
            <button
              key={t.k}
              onClick={() => {
                if (pendingCount > 0 && !window.confirm("Discard unsaved changes and reload the calendar?")) return;
                setFilterCategory(t.k);
              }}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition ${
                on ? "bg-[#1B4332] text-white border-[#1B4332]" : "bg-white text-[#525C58] border-[#E8E8E6] hover:bg-[#F4F4F1]"
              }`}
              data-testid={`trade-filter-${t.k}`}
            >
              <Icon className="h-3 w-3" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Sticky Save bar — shows whenever there are pending edits */}
      {pendingCount > 0 && (
        <div
          className="sticky top-0 z-30 -mx-4 md:-mx-8 lg:-mx-12 px-4 md:px-8 lg:px-12 py-3 bg-amber-50 border-y border-amber-300 flex items-center justify-between gap-3 flex-wrap"
          data-testid="helpers-save-bar"
        >
          <div className="flex items-center gap-2 text-sm text-amber-900">
            <Save className="h-4 w-4" />
            <span><strong data-testid="helpers-pending-count">{pendingCount}</strong> unsaved change{pendingCount === 1 ? "" : "s"}</span>
            <span className="text-xs text-amber-800/80 hidden sm:inline">— click any cell to keep adjusting, then save when ready.</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={discardChanges}
              disabled={saving}
              className="border-amber-300 text-amber-900 hover:bg-amber-100"
              data-testid="helpers-discard-btn"
            >
              <Undo2 className="h-3.5 w-3.5 mr-1" /> Discard
            </Button>
            <Button
              size="sm"
              onClick={saveChanges}
              disabled={saving}
              className="bg-[#1B4332] hover:bg-[#2D6A4F]"
              data-testid="helpers-save-btn"
            >
              <Save className="h-3.5 w-3.5 mr-1" /> {saving ? "Saving…" : `Save ${pendingCount} change${pendingCount === 1 ? "" : "s"}`}
            </Button>
          </div>
        </div>
      )}

      {/* Auto-assign card */}
      <Card className={`border ${data.unassigned_count > 0 ? "border-amber-300 bg-amber-50/40" : "border-[#E8E8E6]"}`} data-testid="auto-assign-card">
        <CardContent className="py-4 flex items-center gap-3 flex-wrap">
          <Wand2 className={`h-5 w-5 shrink-0 ${data.unassigned_count > 0 ? "text-amber-700" : "text-[#7A8A84]"}`} />
          <div className="flex-1 min-w-[200px]">
            <p className="text-sm font-medium">
              {data.unassigned_count > 0
                ? `${data.unassigned_count} segment${data.unassigned_count === 1 ? "" : "s"} have no helper assigned`
                : "Every segment in this window has a helper assigned"}
            </p>
            <p className="text-xs text-[#7A8A84]">
              Auto-assign uses your active cleaner roster. Already-assigned segments are never overwritten.
            </p>
          </div>
          <Select value={strategy} onValueChange={setStrategy}>
            <SelectTrigger className="w-44" data-testid="strategy-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="least_busy">Least busy first</SelectItem>
              <SelectItem value="round_robin">Round-robin</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={autoAssign} disabled={running || data.unassigned_count === 0}
            className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="auto-assign-btn">
            <Sparkles className="h-3.5 w-3.5 mr-1" /> {running ? "Assigning…" : "Auto-assign"}
          </Button>
        </CardContent>
      </Card>

      {/* Calendar grid */}
      <Card className="border-[#E8E8E6]">
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between gap-3 flex-wrap">
            <span>Assignments — {data.from_date} → {data.to_date}</span>
            <span className="inline-flex items-center gap-3 text-[10px] font-normal text-[#7A8A84] flex-wrap">
              <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-emerald-200 border border-emerald-400" /> Booking</span>
              <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-blue-200 border border-blue-400" /> Task</span>
              <span className="inline-flex items-center gap-1"><Repeat className="h-3 w-3 text-purple-700" /> Recurring</span>
              <span className="inline-flex items-center gap-2 ml-1 pl-2 border-l border-[#E8E8E6]" data-testid="leave-legend">
                <span className="uppercase tracking-wider font-medium">On leave</span>
                {LEAVE_KINDS.map((k) => (
                  <span key={k.k} className="inline-flex items-center gap-1">
                    <span className={`inline-block w-2.5 h-2.5 rounded-sm ${k.stripe}`} />
                    {k.label.split(" ")[0]}
                  </span>
                ))}
              </span>
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.cleaners.length === 0 ? (
            <p className="text-sm text-[#7A8A84] text-center py-6">No active house helpers configured. Add some in <a href="/cleaners" className="text-[#1B4332] underline">House Helpers</a>.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px] border-collapse" data-testid="helpers-grid">
                <thead>
                  <tr className="bg-[#F4F4F1] border-b border-[#E8E8E6]">
                    <th className="sticky left-0 bg-[#F4F4F1] z-10 p-2 text-left font-semibold text-[#1C1F1E] border-r border-[#E8E8E6] min-w-[150px]">Helper</th>
                    {days.map((d) => (
                      <th key={fmt(d)} className="p-1.5 text-center font-medium text-[#7A8A84] border-r border-[#E8E8E6] min-w-[56px]">
                        <div className="text-[9px] uppercase">{d.toLocaleDateString(undefined, { weekday: "short" })}</div>
                        <div className="text-xs text-[#1C1F1E]">{d.getDate()}</div>
                      </th>
                    ))}
                    <th className="p-2 text-center text-[#7A8A84] min-w-[80px]">Load</th>
                  </tr>
                </thead>
                <tbody>
                  {data.cleaners.map((c) => (
                    <tr key={c.id}
                        className="border-b border-[#E8E8E6] hover:bg-[#F4F4F1]/30"
                        data-testid={`helper-row-${c.id}`}
                        onDragOver={onRowDragOver}
                        onDrop={(e) => onRowDrop(e, c.id)}>
                      <td className="sticky left-0 bg-white z-10 p-2 border-r border-[#E8E8E6]">
                        <p className="font-medium text-[#1C1F1E]">{c.name}</p>
                        {c.phone && <p className="text-[9px] text-[#7A8A84]">{c.phone}</p>}
                      </td>
                      {days.map((d) => {
                        const ds = fmt(d);
                        const segs = assignmentsFor(c.assignments, ds);
                        const tks = tasksFor(c.tasks, ds);
                        const lv = leaveFor(c.leaves, ds);
                        const lvMeta = lv ? leaveMeta(lv.kind) : null;
                        return (
                          <td key={ds} className={`border-r border-[#E8E8E6] p-0.5 align-top h-14 relative ${lv ? "bg-[repeating-linear-gradient(45deg,_rgba(0,0,0,0.04)_0_4px,_transparent_4px_8px)]" : ""}`} data-testid={`cell-${c.id}-${ds}`} data-on-leave={lv ? lv.kind : undefined} title={lv ? `${lvMeta.label}${lv.notes ? " — " + lv.notes : ""}` : undefined}>
                            {lv && (
                              <div className={`pointer-events-none absolute inset-0 border ${lvMeta.color} opacity-40 rounded-sm`} />
                            )}
                            {lv && (
                              <div className={`relative z-[1] inline-flex items-center gap-1 px-1 py-0.5 mb-0.5 rounded-sm ${lvMeta.color} border text-[9px] font-semibold uppercase tracking-wide`} data-testid={`leave-chip-${c.id}-${ds}`}>
                                {lvMeta.label.split(" ")[0]}
                              </div>
                            )}
                            {segs.map((a) => (
                              <SegmentCell key={a.segment_id || `${a.booking_id}-${a.unit_id}-${a.from_date}`}
                                cell={a} helpers={activeHelpers}
                                onDragStart={(e) => onDragStart(e, { kind: "segment", bookingId: a.booking_id, segmentId: a.segment_id, cleanerId: c.id })}
                                onReassign={(hid) => reassignSegment(a.booking_id, a.segment_id, hid)}
                              />
                            ))}
                            {tks.map((t) => (
                              <TaskCell key={`${t.task_id}-${t.date}`} task={t} helpers={activeHelpers}
                                onDragStart={(e) => onDragStart(e, { kind: "task", taskId: t.task_id, helperId: c.id })}
                                onReassign={(hid) => reassignTask(t.task_id, hid)}
                                onToggleDone={() => markTaskDone(t.task_id, t.date, t.occurrence_status === "done" ? "pending" : "done")}
                              />
                            ))}
                          </td>
                        );
                      })}
                      <td className="text-center font-semibold text-[#1B4332] tabular-nums" data-testid={`load-${c.id}`}>
                        <div>{c.load_nights}n</div>
                        <div className="text-[9px] text-[#7A8A84] font-normal">{c.load_units} unit{c.load_units === 1 ? "" : "s"}</div>
                        {c.load_tasks > 0 && <div className="text-[9px] text-blue-700 font-normal">{c.load_tasks} task{c.load_tasks === 1 ? "" : "s"}</div>}
                        {(c.leaves || []).length > 0 && (
                          <div className="text-[9px] text-amber-700 font-normal" data-testid={`leave-count-${c.id}`}>
                            {c.leaves.length} leave{c.leaves.length === 1 ? "" : "s"}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {/* Unassigned row — drop target to detach a cell */}
                  <tr className="border-b border-amber-200 bg-amber-50/30"
                      data-testid="row-unassigned"
                      onDragOver={onRowDragOver}
                      onDrop={(e) => onRowDrop(e, null)}>
                    <td className="sticky left-0 bg-amber-50/80 z-10 p-2 border-r border-[#E8E8E6]">
                      <p className="font-medium text-amber-900 flex items-center gap-1"><UserX className="h-3.5 w-3.5" /> Unassigned</p>
                      <p className="text-[9px] text-amber-800">Drop here to clear</p>
                    </td>
                    {days.map((d) => {
                      const ds = fmt(d);
                      const segs = (data.unassigned || []).filter((a) => a.from_date <= ds && a.to_date > ds);
                      const tks = (data.unassigned_tasks || []).filter((t) => t.date === ds);
                      return (
                        <td key={ds} className="border-r border-[#E8E8E6] p-0.5 align-top h-14" data-testid={`cell-unassigned-${ds}`}>
                          {segs.map((a) => (
                            <SegmentCell key={a.segment_id || `${a.booking_id}-${a.unit_id}-${a.from_date}`}
                              cell={a} helpers={activeHelpers} unassigned
                              onDragStart={(e) => onDragStart(e, { kind: "segment", bookingId: a.booking_id, segmentId: a.segment_id, cleanerId: null })}
                              onReassign={(hid) => reassignSegment(a.booking_id, a.segment_id, hid)}
                            />
                          ))}
                          {tks.map((t) => (
                            <TaskCell key={`${t.task_id}-${t.date}`} task={t} helpers={activeHelpers} unassigned
                              onDragStart={(e) => onDragStart(e, { kind: "task", taskId: t.task_id, helperId: null })}
                              onReassign={(hid) => reassignTask(t.task_id, hid)}
                              onToggleDone={() => markTaskDone(t.task_id, t.date, t.occurrence_status === "done" ? "pending" : "done")}
                            />
                          ))}
                        </td>
                      );
                    })}
                    <td className="text-center font-semibold text-amber-900 tabular-nums" data-testid="load-unassigned">
                      <div>{data.unassigned_count || 0}</div>
                      {(data.unassigned_tasks_count || 0) > 0 && <div className="text-[9px] text-blue-700 font-normal">{data.unassigned_tasks_count} task{data.unassigned_tasks_count === 1 ? "" : "s"}</div>}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {(data.unassigned.length > 0 || (data.unassigned_tasks || []).length > 0) && (
        <Card className="border-amber-200" data-testid="unassigned-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2 text-amber-900">
              <UserX className="h-4 w-4" /> Unassigned ({data.unassigned.length + (data.unassigned_tasks || []).length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            {data.unassigned.length > 0 && (
              <div>
                <p className="font-medium mb-2 text-[#1C1F1E] flex items-center gap-1"><Home className="h-3.5 w-3.5" /> Booking segments</p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#E8E8E6] text-[#7A8A84]">
                      <th className="text-left p-2 font-medium">Visitor</th>
                      <th className="text-left p-2 font-medium">Unit</th>
                      <th className="text-left p-2 font-medium">Dates</th>
                      <th className="text-left p-2 font-medium">Nights</th>
                      <th className="text-right p-2 font-medium">Assign</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.unassigned.map((a, i) => (
                      <tr key={i} className="border-b border-[#E8E8E6]" data-testid={`unassigned-row-${i}`}>
                        <td className="p-2 font-medium">{a.visitor_name}</td>
                        <td className="p-2">{a.unit_name}</td>
                        <td className="p-2 text-[#525C58]">{a.from_date} → {a.to_date}</td>
                        <td className="p-2">{a.nights}</td>
                        <td className="p-2 text-right">
                          <Select onValueChange={(v) => reassignSegment(a.booking_id, a.segment_id, v)}>
                            <SelectTrigger className="w-44 inline-flex"><SelectValue placeholder="Assign…" /></SelectTrigger>
                            <SelectContent>
                              {activeHelpers.map((h) => (
                                <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {(data.unassigned_tasks || []).length > 0 && (
              <div>
                <p className="font-medium mb-2 text-[#1C1F1E] flex items-center gap-1"><ClipboardCheck className="h-3.5 w-3.5" /> Helper tasks</p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#E8E8E6] text-[#7A8A84]">
                      <th className="text-left p-2 font-medium">Title</th>
                      <th className="text-left p-2 font-medium">Date</th>
                      <th className="text-left p-2 font-medium">Unit</th>
                      <th className="text-left p-2 font-medium">Priority</th>
                      <th className="text-right p-2 font-medium">Assign</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.unassigned_tasks || []).map((t, i) => (
                      <tr key={i} className="border-b border-[#E8E8E6]" data-testid={`unassigned-task-${i}`}>
                        <td className="p-2 font-medium flex items-center gap-1">{t.kind === "recurring" ? <Repeat className="h-3 w-3 text-purple-700" /> : null}{t.title}</td>
                        <td className="p-2 text-[#525C58]">{t.date}</td>
                        <td className="p-2">{t.unit_name || "—"}</td>
                        <td className="p-2"><Badge className="bg-slate-100 text-slate-700 border border-slate-200 text-[10px]">{t.priority}</Badge></td>
                        <td className="p-2 text-right">
                          <Select onValueChange={(v) => reassignTask(t.task_id, v)}>
                            <SelectTrigger className="w-44 inline-flex"><SelectValue placeholder="Assign…" /></SelectTrigger>
                            <SelectContent>
                              {activeHelpers.map((h) => (
                                <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Phase 53 — Email schedule dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="max-w-xl" data-testid="email-schedule-dialog">
          <DialogHeader>
            <DialogTitle>Email schedule</DialogTitle>
            <DialogDescription>
              Sends the {filterCategory === "all" ? "full" : tradeMeta(filterCategory).label} schedule for <strong>{from} → {to}</strong> as a PDF attachment via Resend.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm">Send to staff</Label>
              <div className="flex flex-wrap gap-1.5 max-h-44 overflow-y-auto p-2 border border-[#E8E8E6] rounded-lg" data-testid="email-staff-picker">
                {(cleaners || []).filter((c) => c.active).map((c) => {
                  const on = emailDraft.staff_ids.includes(c.id);
                  const noEmail = !c.email;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => {
                        const cur = new Set(emailDraft.staff_ids);
                        if (cur.has(c.id)) cur.delete(c.id); else cur.add(c.id);
                        setEmailDraft((d) => ({ ...d, staff_ids: [...cur] }));
                      }}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium border transition ${
                        on ? "bg-[#1B4332] text-white border-[#1B4332]" : noEmail ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-white text-[#525C58] border-[#E8E8E6] hover:bg-[#F4F4F1]"
                      }`}
                      title={noEmail ? "No email on file — will be skipped" : c.email}
                      data-testid={`email-staff-${c.id}`}
                    >
                      {c.name}{noEmail ? " ⚠️" : ""}
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-[#7A8A84]">Staff highlighted in amber have no email on file — they'll be skipped on send.</p>
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Other emails (foreman, manager…)</Label>
              <Textarea
                rows={2}
                placeholder="comma- or space-separated, e.g. foreman@hospital.org, plumber@vendor.com"
                value={emailDraft.extra_recipients}
                onChange={(e) => setEmailDraft((d) => ({ ...d, extra_recipients: e.target.value }))}
                data-testid="email-extra-recipients"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Subject (optional)</Label>
              <Input
                placeholder="Defaults to: Operations Schedule — <trade> (<dates>)"
                value={emailDraft.subject}
                onChange={(e) => setEmailDraft((d) => ({ ...d, subject: e.target.value }))}
                data-testid="email-subject-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Message (optional)</Label>
              <Textarea
                rows={3}
                placeholder="A short note to include above the schedule…"
                value={emailDraft.message}
                onChange={(e) => setEmailDraft((d) => ({ ...d, message: e.target.value }))}
                data-testid="email-message-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)}>Cancel</Button>
            <Button onClick={sendScheduleEmail} disabled={emailSending} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="email-send-btn">
              <Mail className="h-3.5 w-3.5 mr-1" /> {emailSending ? "Sending…" : "Send schedule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SegmentCell({ cell, helpers, unassigned, onDragStart, onReassign }) {
  const base = unassigned
    ? "bg-amber-50 text-amber-900 border border-amber-300"
    : "bg-emerald-100 text-emerald-900 border border-emerald-300";
  return (
    <Popover>
      <PopoverTrigger asChild>
        <div draggable onDragStart={onDragStart}
          className={`rounded-sm ${base} px-1 py-0.5 text-[9px] font-medium truncate mb-0.5 cursor-grab active:cursor-grabbing hover:brightness-95`}
          title={`${cell.unit_name} · ${cell.visitor_name} · ${cell.from_date}→${cell.to_date}`}
          data-testid={`segment-cell-${cell.segment_id || cell.booking_id}`}>
          <Home className="h-2.5 w-2.5 inline-block align-[-1px] mr-0.5" />
          {(cell.unit_name || "").split(" ")[0]} · {(cell.visitor_name || "").split(" ")[0]}
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2 text-xs" data-testid="segment-popover">
        <div className="mb-2 p-2 rounded bg-[#F4F4F1]">
          <p className="font-semibold text-[#1C1F1E]">{cell.unit_name}</p>
          <p className="text-[10px] text-[#7A8A84]">{cell.visitor_name} · {cell.from_date} → {cell.to_date} · {cell.nights}n</p>
        </div>
        <p className="text-[10px] text-[#7A8A84] mb-1">Change helper:</p>
        <div className="space-y-1">
          {helpers.map((h) => (
            <button key={h.id} onClick={() => onReassign(h.id)}
              data-testid={`popover-helper-${h.id}`}
              className="w-full text-left px-2 py-1 rounded hover:bg-[#F4F4F1] text-[#1C1F1E]">
              {h.name}
            </button>
          ))}
          <button onClick={() => onReassign(null)}
            data-testid="popover-clear"
            className="w-full text-left px-2 py-1 rounded hover:bg-red-50 text-red-700 inline-flex items-center gap-1">
            <X className="h-3 w-3" /> Clear helper
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function TaskCell({ task, helpers, unassigned, onDragStart, onReassign, onToggleDone }) {
  const done = task.occurrence_status === "done";
  const trade = tradeMeta(task.category || "housekeeping");
  // Trade-specific tint when it's not the legacy housekeeping bucket. Done
  // tasks always get the muted emerald look so coordinators spot completed
  // work in any trade at a glance.
  const tradeBase = task.category && task.category !== "housekeeping"
    ? `${trade.color}`
    : (unassigned ? "bg-blue-50 text-blue-900 border border-blue-200" : "bg-blue-100 text-blue-900 border border-blue-300");
  const base = done
    ? "bg-emerald-50 text-emerald-800 border border-emerald-200 line-through"
    : tradeBase;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <div draggable onDragStart={onDragStart}
          className={`rounded-sm ${base} px-1 py-0.5 text-[9px] font-medium truncate mb-0.5 cursor-grab active:cursor-grabbing hover:brightness-95`}
          title={`${task.title}${task.description ? ` — ${task.description}` : ""}${task.start_time ? ` · ${task.start_time}` : ""}`}
          data-testid={`task-cell-${task.task_id}-${task.date}`}>
          {task.kind === "recurring" ? <Repeat className="h-2.5 w-2.5 inline-block align-[-1px] mr-0.5" /> : <ClipboardCheck className="h-2.5 w-2.5 inline-block align-[-1px] mr-0.5" />}
          {task.title}
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2 text-xs" data-testid="task-popover">
        <div className="mb-2 p-2 rounded bg-[#F4F4F1]">
          <p className="font-semibold text-[#1C1F1E]">{task.title}</p>
          {task.description && <p className="text-[10px] text-[#7A8A84]">{task.description}</p>}
          <p className="text-[10px] text-[#7A8A84]">
            {task.date}{task.start_time ? ` · ${task.start_time}${task.end_time ? `–${task.end_time}` : ""}` : ""}
            {task.kind === "recurring" && <> · <Repeat className="h-2.5 w-2.5 inline" /> recurring</>}
          </p>
        </div>
        <button onClick={onToggleDone}
          data-testid={`popover-task-${done ? "undo" : "done"}-${task.task_id}`}
          className={`w-full text-left px-2 py-1.5 rounded inline-flex items-center gap-1 ${done ? "hover:bg-slate-100 text-slate-600" : "hover:bg-emerald-50 text-emerald-700"}`}>
          <CheckCircle2 className="h-3.5 w-3.5" /> {done ? "Mark pending" : "Mark done"}
        </button>
        <div className="my-1 border-t border-[#E8E8E6]" />
        <p className="text-[10px] text-[#7A8A84] mb-1">Change helper:</p>
        <div className="space-y-1 max-h-40 overflow-y-auto">
          {helpers.map((h) => (
            <button key={h.id} onClick={() => onReassign(h.id)}
              data-testid={`popover-task-helper-${h.id}`}
              className="w-full text-left px-2 py-1 rounded hover:bg-[#F4F4F1] text-[#1C1F1E]">
              {h.name}
            </button>
          ))}
          <button onClick={() => onReassign(null)}
            data-testid="popover-task-clear"
            className="w-full text-left px-2 py-1 rounded hover:bg-red-50 text-red-700 inline-flex items-center gap-1">
            <X className="h-3 w-3" /> Unassign task
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function todayIso() { return todayLocalIso(); }
function addDays(iso, n) { return addDaysIso(iso, n); }
